import {retry} from 'es-toolkit';
import {injectable} from 'inversify';

import type {AnyAttachmentModel} from '../../../modules/chats/attachment/types';
import type {CreatePostConfig} from '../../../modules/chats/channel/models/Channel/PostsManager/types';
import type {
  AttachFileConfig,
  FileOrPath,
} from '../../../modules/chats/editor/models/Editor/types';
import type {Nullable} from '../../../utils/utility-types';

import {postExpandCommon, postStore} from '../../../api/channel/post';
import {fileStore} from '../../../api/media/file';
import {FileAttachmentModel} from '../../../modules/chats/attachment/models/FileAttachment/FileAttachment.model';
import {getUuid} from '../../../utils/getUuid';
import {logger} from '../../../utils/logger';
import {storage} from '../../../utils/storage';
import {BlobDBService} from '../../application/BlobDBService';

const POSTS_STORAGE_KEY = 'pendingPosts';

@injectable()
export class PendingPostsService {
  private blobDBService = new BlobDBService({
    dbName: 'ych-pending-posts-db',
    loggingPrefix: '[PendingPostsService]',
  });

  constructor() {
    this.blobDBService.init();
  }

  async add({channelId, config, uuid}: PendingPostsService.SavePostParams) {
    if (!this.blobDBService.isEnabled) {
      // In this case we can't save files, so let's save the message only if it has a body
      if (config.body) {
        await this.savePostToStorage({
          attachments: [],
          body: config.body,
          channelId,
          createdAt: Date.now(),
          uuid,
        });
      }

      return;
    }

    const attachments = await Promise.all(
      config.attachments.map(async attachment => {
        if (
          this.isUnloadedFileAttachment(attachment) &&
          attachment.fileConfig
        ) {
          return this.saveFile(attachment.fileConfig);
        } else {
          return attachment.id;
        }
      }),
    );

    await this.savePostToStorage({
      attachments,
      body: config.body,
      channelId,
      createdAt: Date.now(),
      uuid,
    });
  }

  async remove(uuid: string) {
    const pendingPosts = await this.readPendingPostsFromStorage();
    const attachments = pendingPosts[uuid]?.attachments ?? [];

    try {
      await Promise.all(
        attachments.map(async attachment => {
          if (
            typeof attachment !== 'string' &&
            typeof attachment.file === 'string'
          ) {
            return this.blobDBService.remove(attachment.file);
          }
        }),
      );
    } catch (e) {
      logger.error(e);
    }

    delete pendingPosts[uuid];
    await this.savePendingPostsToStorage(pendingPosts);
  }

  async uploadPendingPosts() {
    const pendingPostsCache = await this.readPendingPostsFromStorage();

    const posts = Object.values(pendingPostsCache).sort(
      (a, b) => a.createdAt - b.createdAt,
    );

    for (const post of posts) {
      await this.uploadPost(post);
    }
  }

  private isUnloadedFileAttachment(
    attachment: AnyAttachmentModel,
  ): attachment is FileAttachmentModel {
    return attachment instanceof FileAttachmentModel && !attachment.isUploaded;
  }

  private async readFile(
    attachFile: AttachFileConfig,
  ): Promise<Nullable<AttachFileConfig>> {
    if (typeof attachFile.file !== 'string') {
      return attachFile;
    }

    const file = await this.blobDBService.get(attachFile.file);

    if (!file) {
      logger.error(
        `Failed to read file from storage. File with id: ${attachFile.file} not found.`,
      );

      return null;
    }

    return {
      ...attachFile,
      file: file as FileOrPath,
    };
  }

  private async readPendingPostsFromStorage(): Promise<PendingPostsService.PendingPostsCache> {
    const value = await storage.getItem(POSTS_STORAGE_KEY);

    if (!value) {
      return {};
    }

    return JSON.parse(value) as PendingPostsService.PendingPostsCache;
  }

  private async saveFile(
    attachFile: AttachFileConfig,
  ): Promise<AttachFileConfig> {
    const {name} = attachFile.meta;
    const key = `${getUuid()}_${name}`;

    const savedFileKey = await this.blobDBService.add(key, attachFile.file, {
      shouldCopyFileToTmp: true,
    });

    return {
      ...attachFile,
      file: savedFileKey,
    };
  }

  private savePendingPostsToStorage(
    posts: PendingPostsService.PendingPostsCache,
  ) {
    const serializedValue = JSON.stringify(posts);

    return storage.setItem(POSTS_STORAGE_KEY, serializedValue);
  }

  private async savePostToStorage(post: PendingPostsService.CachedPostData) {
    const pendingPosts = await this.readPendingPostsFromStorage();

    return this.savePendingPostsToStorage({
      ...pendingPosts,
      [post.uuid]: post,
    });
  }

  private async uploadFile({file, meta}: AttachFileConfig) {
    const {mimetype, name} = meta;

    try {
      const response = await retry(
        () =>
          fileStore.upload(file, {
            mimetype,
            name,
          }),
        {
          delay: i => Math.pow(2, i) * 10 * 1000,
          retries: 5,
        },
      );

      return response;
    } catch (e) {
      logger.error(e);

      return null;
    }
  }

  private async uploadPost({
    attachments,
    body,
    channelId,
    uuid,
  }: PendingPostsService.CachedPostData) {
    const promisesByIndex = new Map<number, number>();
    const filesToUpload: AttachFileConfig[] = [];
    // If we upload files successfully we need to remove them from local db with these keys
    const fileToUploadKeys: string[] = [];

    const attachmentsWithLoadedFiles = await Promise.all(
      attachments.map(async attachment => {
        // If attachment is loaded (attachment is id)
        if (typeof attachment === 'string') {
          return attachment;
        }

        // If we have key for load file from local db
        if (typeof attachment.file === 'string') {
          fileToUploadKeys.push(attachment.file);

          return this.readFile(attachment);
        }

        return null;
      }),
    );

    attachmentsWithLoadedFiles.forEach((attachment, index) => {
      if (attachment && typeof attachment !== 'string') {
        promisesByIndex.set(index, filesToUpload.length);
        filesToUpload.push(attachment);
      }
    });

    let attachmentIds: string[] = [];

    if (filesToUpload.length) {
      const uploadedFiles = await Promise.all(
        filesToUpload.map(file => this.uploadFile(file)),
      );

      if (!uploadedFiles.length || uploadedFiles.some(file => !file)) {
        logger.error('Failed to upload pending files');

        return;
      }

      attachmentIds = attachments
        .map((attachment, index) => {
          if (typeof attachment === 'string') {
            return attachment;
          }

          const indexInUploadedFiles = promisesByIndex.get(index);

          if (indexInUploadedFiles === undefined) {
            return null;
          }

          const id = uploadedFiles[indexInUploadedFiles]?._id;

          if (!id) {
            return null;
          }

          return uploadedFiles[promisesByIndex.get(index)!]!._id;
        })
        .filter(Boolean);
    }

    try {
      await retry(
        () =>
          postStore.create({
            attachment_ids: attachmentIds,
            body,
            channel_id: channelId,
            expand: postExpandCommon,
            uuid,
          }),
        {
          /**
           * Exponential backoff pattern:
           *
           * 1st retry: 2^1 * 10 = 20s
           * 2nd retry: 2^2 * 10 = 40s
           * 3rd retry: 2^3 * 10 = 80s
           * 4th retry: 2^4 * 10 = 160s
           * 5th retry: 2^5 * 10 = 320s
           *
           * Total max wait time: 620s (10.3 minutes)
           */
          delay: i => Math.pow(2, i) * 10 * 1000,
          retries: 5,
        },
      );
    } catch (e) {
      logger.error(e);
    } finally {
      this.remove(uuid);
      fileToUploadKeys.forEach(key => this.blobDBService.remove(key));
    }
  }
}

export namespace PendingPostsService {
  export type CachedPostData = {
    /**
     * attachment id or file to upload
     */
    attachments: (AttachFileConfig | string)[];
    body: string;
    channelId: string;
    createdAt: number;
    uuid: string;
  };

  export type PendingPostsCache = Record<string, CachedPostData>;
  export type SavePostParams = {
    channelId: string;
    config: CreatePostConfig;
    uuid: string;
  };
}
